import { Tool } from '@modelcontextprotocol/sdk/types.js';
import axios from 'axios';
import {
  ApiDocsInputSchema,
  type Config,
} from '../types/index.js';
import { readText } from '../utils/fs.js';
import { findSmartStackStructure, findControllerFiles } from '../lib/detector.js';
import { logger } from '../lib/logger.js';
import path from 'path';

export const apiDocsTool: Tool = {
  name: 'api_docs',
  description: 'Get API documentation for SmartStack endpoints. Can fetch from Swagger/OpenAPI or parse controller files directly.',
  inputSchema: {
    type: 'object',
    properties: {
      endpoint: {
        type: 'string',
        description: 'Filter by endpoint path (e.g., "/api/users"). Leave empty for all endpoints.',
      },
      format: {
        type: 'string',
        enum: ['markdown', 'json', 'openapi'],
        description: 'Output format',
        default: 'markdown',
      },
      controller: {
        type: 'string',
        description: 'Filter by controller name (e.g., "Users")',
      },
    },
  },
};

interface EndpointInfo {
  method: string;
  path: string;
  controller: string;
  action: string;
  summary?: string;
  parameters: ParameterInfo[];
  requestBody?: RequestBodyInfo;
  responses: ResponseInfo[];
  authorize: boolean;
  example?: ExampleInfo;
}

interface RequestBodyInfo {
  type: string;
  required: boolean;
  schema?: Record<string, string>;
}

interface ExampleInfo {
  request?: string;
  response?: string;
}

interface ParameterInfo {
  name: string;
  in: 'path' | 'query' | 'header' | 'body';
  type: string;
  required: boolean;
  description?: string;
}

interface ResponseInfo {
  status: number;
  type?: string;
  description?: string;
}

export async function handleApiDocs(
  args: unknown,
  config: Config
): Promise<string> {
  const input = ApiDocsInputSchema.parse(args);

  logger.info('Fetching API documentation', { endpoint: input.endpoint, format: input.format });

  let endpoints: EndpointInfo[] = [];

  // Try to fetch from Swagger first if API is enabled
  if (config.smartstack.apiEnabled && config.smartstack.apiUrl) {
    try {
      endpoints = await fetchFromSwagger(config.smartstack.apiUrl);
      logger.debug('Fetched endpoints from Swagger', { count: endpoints.length });
    } catch (error) {
      logger.warn('Failed to fetch from Swagger, falling back to code parsing', { error });
    }
  }

  // Fall back to parsing controller files
  if (endpoints.length === 0) {
    const structure = await findSmartStackStructure(config.smartstack.projectPath);
    endpoints = await parseControllers(structure);
  }

  // Filter endpoints
  if (input.endpoint) {
    const filter = input.endpoint.toLowerCase();
    endpoints = endpoints.filter(e => e.path.toLowerCase().includes(filter));
  }

  if (input.controller) {
    const filter = input.controller.toLowerCase();
    endpoints = endpoints.filter(e => e.controller.toLowerCase().includes(filter));
  }

  // Format output
  switch (input.format) {
    case 'json':
      return JSON.stringify(endpoints, null, 2);
    case 'openapi':
      return formatAsOpenApi(endpoints);
    case 'markdown':
    default:
      return formatAsMarkdown(endpoints);
  }
}

async function fetchFromSwagger(apiUrl: string): Promise<EndpointInfo[]> {
  const swaggerUrl = `${apiUrl}/swagger/v1/swagger.json`;

  const response = await axios.get(swaggerUrl, {
    timeout: 5000,
    httpsAgent: new (await import('https')).Agent({ rejectUnauthorized: false }),
  });

  const spec = response.data;
  const endpoints: EndpointInfo[] = [];

  for (const [pathKey, pathItem] of Object.entries(spec.paths || {})) {
    const pathObj = pathItem as Record<string, unknown>;

    for (const method of ['get', 'post', 'put', 'patch', 'delete']) {
      const operation = pathObj[method] as Record<string, unknown> | undefined;
      if (!operation) continue;

      const tags = (operation.tags as string[]) || ['Unknown'];
      const parameters = (operation.parameters as Array<Record<string, unknown>>) || [];

      endpoints.push({
        method: method.toUpperCase(),
        path: pathKey,
        controller: tags[0],
        action: (operation.operationId as string) || method,
        summary: operation.summary as string | undefined,
        parameters: parameters.map(p => ({
          name: p.name as string,
          in: p.in as 'path' | 'query' | 'header' | 'body',
          type: (p.schema as Record<string, string>)?.type || 'string',
          required: p.required as boolean || false,
          description: p.description as string | undefined,
        })),
        requestBody: operation.requestBody ? { type: 'object', required: true } : undefined,
        responses: Object.entries(operation.responses || {}).map(([status, resp]) => ({
          status: parseInt(status, 10),
          description: (resp as Record<string, string>).description,
        })),
        authorize: !!(operation.security && (operation.security as unknown[]).length > 0),
      });
    }
  }

  return endpoints;
}

async function parseControllers(
  structure: ReturnType<typeof findSmartStackStructure> extends Promise<infer T> ? T : never
): Promise<EndpointInfo[]> {
  if (!structure.api) {
    return [];
  }

  const controllerFiles = await findControllerFiles(structure.api);
  const endpoints: EndpointInfo[] = [];

  for (const file of controllerFiles) {
    const content = await readText(file);
    const fileName = path.basename(file, '.cs');
    const controllerName = fileName.replace('Controller', '');

    // Parse route attribute
    const routeMatch = content.match(/\[Route\s*\(\s*"([^"]+)"\s*\)\]/);
    const baseRoute = routeMatch ? routeMatch[1].replace('[controller]', controllerName.toLowerCase()) : `/api/${controllerName.toLowerCase()}`;

    // Parse authorize attribute
    const hasAuthorize = content.includes('[Authorize]');

    // Parse HTTP methods
    const httpMethods = [
      { pattern: /\[HttpGet(?:\s*\(\s*"([^"]*)"\s*\))?\]/g, method: 'GET' },
      { pattern: /\[HttpPost(?:\s*\(\s*"([^"]*)"\s*\))?\]/g, method: 'POST' },
      { pattern: /\[HttpPut(?:\s*\(\s*"([^"]*)"\s*\))?\]/g, method: 'PUT' },
      { pattern: /\[HttpPatch(?:\s*\(\s*"([^"]*)"\s*\))?\]/g, method: 'PATCH' },
      { pattern: /\[HttpDelete(?:\s*\(\s*"([^"]*)"\s*\))?\]/g, method: 'DELETE' },
    ];

    for (const { pattern, method } of httpMethods) {
      let match;
      while ((match = pattern.exec(content)) !== null) {
        const routeSuffix = match[1] || '';
        const fullPath = routeSuffix ? `${baseRoute}/${routeSuffix}` : baseRoute;

        // Try to find the method name after the attribute
        const afterAttribute = content.substring(match.index);
        const methodMatch = afterAttribute.match(/public\s+(?:async\s+)?(?:Task<)?(?:ActionResult<)?(\w+)(?:>)?\s+(\w+)\s*\(/);

        const parameters: ParameterInfo[] = [];

        // Parse path parameters
        const pathParams = fullPath.match(/\{(\w+)(?::\w+)?\}/g);
        if (pathParams) {
          for (const param of pathParams) {
            const paramName = param.replace(/[{}:]/g, '').replace(/\w+$/, '');
            parameters.push({
              name: paramName || param.replace(/[{}]/g, '').split(':')[0],
              in: 'path',
              type: 'string',
              required: true,
            });
          }
        }

        // Parse [FromBody] parameters and extract request body info
        let requestBody: RequestBodyInfo | undefined;
        if (afterAttribute.includes('[FromBody]')) {
          const bodyMatch = afterAttribute.match(/\[FromBody\]\s*(\w+)\s+(\w+)/);
          if (bodyMatch) {
            requestBody = {
              type: bodyMatch[1],
              required: true,
            };
            parameters.push({
              name: bodyMatch[2],
              in: 'body',
              type: bodyMatch[1],
              required: true,
            });
          }
        }

        // Extract XML summary comment
        let summary: string | undefined;
        const methodStartIndex = content.lastIndexOf('/// <summary>', match.index);
        if (methodStartIndex !== -1 && match.index - methodStartIndex < 500) {
          const summaryMatch = content.substring(methodStartIndex, match.index).match(/\/\/\/\s*<summary>\s*\n?\s*\/\/\/\s*(.+?)\s*\n?\s*\/\/\/\s*<\/summary>/s);
          if (summaryMatch) {
            summary = summaryMatch[1].replace(/\/\/\/\s*/g, '').trim();
          }
        }

        // Parse [FromQuery] parameters
        const queryMatches = afterAttribute.matchAll(/\[FromQuery\]\s*(\w+)\s+(\w+)/g);
        for (const qm of queryMatches) {
          parameters.push({
            name: qm[2],
            in: 'query',
            type: qm[1],
            required: false,
          });
        }

        // Generate example based on method
        const example: ExampleInfo = {};
        if (requestBody) {
          example.request = generateExampleRequest(requestBody.type);
        }
        if (method === 'GET' || method === 'POST') {
          example.response = generateExampleResponse(controllerName, method);
        }

        endpoints.push({
          method,
          path: fullPath.replace(/\/+/g, '/'),
          controller: controllerName,
          action: methodMatch ? methodMatch[2] : 'Unknown',
          summary,
          parameters,
          requestBody,
          responses: generateResponses(method),
          authorize: hasAuthorize,
          example,
        });
      }
    }
  }

  return endpoints;
}

function generateResponses(method: string): ResponseInfo[] {
  switch (method) {
    case 'GET':
      return [
        { status: 200, description: 'Success' },
        { status: 404, description: 'Not Found' },
      ];
    case 'POST':
      return [
        { status: 201, description: 'Created' },
        { status: 400, description: 'Bad Request' },
        { status: 422, description: 'Validation Error' },
      ];
    case 'PUT':
    case 'PATCH':
      return [
        { status: 204, description: 'No Content' },
        { status: 400, description: 'Bad Request' },
        { status: 404, description: 'Not Found' },
      ];
    case 'DELETE':
      return [
        { status: 204, description: 'No Content' },
        { status: 404, description: 'Not Found' },
      ];
    default:
      return [{ status: 200, description: 'Success' }];
  }
}

function generateExampleRequest(typeName: string): string {
  // Generate example based on DTO type name
  const name = typeName.replace('Request', '').replace('Dto', '').replace('Create', '').replace('Update', '');
  const example: Record<string, unknown> = {
    code: `${name.toLowerCase()}_001`,
  };

  if (typeName.includes('Create')) {
    example.name = `New ${name}`;
    example.description = `Description for ${name}`;
  } else if (typeName.includes('Update')) {
    example.name = `Updated ${name}`;
    example.description = `Updated description for ${name}`;
  }

  return JSON.stringify(example, null, 2);
}

function generateExampleResponse(controllerName: string, method: string): string {
  const example: Record<string, unknown> = {
    id: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
    tenantId: '6fa85f64-5717-4562-b3fc-2c963f66afa6',
    code: `${controllerName.toLowerCase()}_001`,
    name: `Example ${controllerName}`,
    createdAt: new Date().toISOString(),
    updatedAt: null,
    createdBy: 'user@example.com',
  };

  if (method === 'GET') {
    return JSON.stringify(example, null, 2);
  }

  return JSON.stringify(example, null, 2);
}

function formatAsMarkdown(endpoints: EndpointInfo[]): string {
  const lines: string[] = [];

  lines.push('# SmartStack API Documentation');
  lines.push('');
  lines.push(`Generated: ${new Date().toISOString()}`);
  lines.push('');

  // Group by controller
  const byController = new Map<string, EndpointInfo[]>();
  for (const endpoint of endpoints) {
    const existing = byController.get(endpoint.controller) || [];
    existing.push(endpoint);
    byController.set(endpoint.controller, existing);
  }

  for (const [controller, controllerEndpoints] of byController) {
    lines.push(`## ${controller}`);
    lines.push('');

    for (const endpoint of controllerEndpoints) {
      const authBadge = endpoint.authorize ? ' 🔒' : '';
      lines.push(`### \`${endpoint.method}\` ${endpoint.path}${authBadge}`);
      lines.push('');

      if (endpoint.summary) {
        lines.push(endpoint.summary);
        lines.push('');
      }

      if (endpoint.parameters.length > 0) {
        lines.push('**Parameters:**');
        lines.push('');
        lines.push('| Name | In | Type | Required |');
        lines.push('|------|-----|------|----------|');
        for (const param of endpoint.parameters) {
          lines.push(`| ${param.name} | ${param.in} | ${param.type} | ${param.required ? 'Yes' : 'No'} |`);
        }
        lines.push('');
      }

      if (endpoint.requestBody) {
        lines.push('**Request Body:**');
        lines.push('');
        lines.push(`Type: \`${endpoint.requestBody.type}\``);
        lines.push('');
        if (endpoint.example?.request) {
          lines.push('```json');
          lines.push(endpoint.example.request);
          lines.push('```');
          lines.push('');
        }
      }

      if (endpoint.responses.length > 0) {
        lines.push('**Responses:**');
        lines.push('');
        for (const resp of endpoint.responses) {
          lines.push(`- \`${resp.status}\`: ${resp.description || 'No description'}`);
        }
        lines.push('');
      }

      if (endpoint.example?.response && (endpoint.method === 'GET' || endpoint.method === 'POST')) {
        lines.push('**Example Response:**');
        lines.push('');
        lines.push('```json');
        lines.push(endpoint.example.response);
        lines.push('```');
        lines.push('');
      }
    }
  }

  if (endpoints.length === 0) {
    lines.push('No endpoints found matching the filter criteria.');
  }

  // Add summary statistics
  lines.push('---');
  lines.push('');
  lines.push('## Summary');
  lines.push('');
  lines.push(`- **Total Endpoints**: ${endpoints.length}`);
  lines.push(`- **Controllers**: ${new Set(endpoints.map(e => e.controller)).size}`);
  lines.push(`- **Authenticated**: ${endpoints.filter(e => e.authorize).length}`);
  lines.push(`- **Public**: ${endpoints.filter(e => !e.authorize).length}`);

  return lines.join('\n');
}

function formatAsOpenApi(endpoints: EndpointInfo[]): string {
  const spec = {
    openapi: '3.0.0',
    info: {
      title: 'SmartStack API',
      version: '1.0.0',
    },
    paths: {} as Record<string, Record<string, unknown>>,
  };

  for (const endpoint of endpoints) {
    if (!spec.paths[endpoint.path]) {
      spec.paths[endpoint.path] = {};
    }

    spec.paths[endpoint.path][endpoint.method.toLowerCase()] = {
      tags: [endpoint.controller],
      operationId: endpoint.action,
      summary: endpoint.summary,
      parameters: endpoint.parameters
        .filter(p => p.in !== 'body')
        .map(p => ({
          name: p.name,
          in: p.in,
          required: p.required,
          schema: { type: p.type },
        })),
      responses: Object.fromEntries(
        endpoint.responses.map(r => [
          r.status.toString(),
          { description: r.description || 'Response' },
        ])
      ),
      security: endpoint.authorize ? [{ bearerAuth: [] }] : undefined,
    };
  }

  return JSON.stringify(spec, null, 2);
}
